import React, { useEffect, useState } from 'react';
import { Plus, Edit, Trash2, FileText, Mail, Send, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/context/AuthContext';
import AnalyticsTab from '@/components/AnalyticsTab';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  paid: 'bg-green-100 text-green-800',
  processing: 'bg-blue-100 text-blue-800',
  shipped: 'bg-purple-100 text-purple-800',
  delivered: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800'
};

export default function ArtistDashboardPage() {
  const { getAuthHeaders } = useAuth();
  const [artworks, setArtworks] = useState([]);
  const [orders, setOrders] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [seriesWithDescriptions, setSeriesWithDescriptions] = useState([]);
  const [contactMessages, setContactMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [seriesDialogOpen, setSeriesDialogOpen] = useState(false);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [editingSeriesName, setEditingSeriesName] = useState('');
  const [editingSeriesDescription, setEditingSeriesDescription] = useState('');
  const [editingArtwork, setEditingArtwork] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'painting',
    gallery_type: 'main',
    series_name: '',
    price: '',
    image_url: '',
    dimensions: '',
    shipping_rate: '15.00',
    quantity_available: '1',
    includes_certificate: true,
    is_framed: false,
    is_signed: true
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [existingSeries, setExistingSeries] = useState([]);

  useEffect(() => {
    fetchData();
    fetchSeries();
    fetchSeriesWithDescriptions();
    fetchContactMessages();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [artworksRes, ordersRes, subscribersRes] = await Promise.all([
        axios.get(`${API}/artworks?available=false`, getAuthHeaders()),
        axios.get(`${API}/orders/all`, getAuthHeaders()),
        axios.get(`${API}/newsletter/subscribers`, getAuthHeaders())
      ]);
      setArtworks(artworksRes.data);
      setOrders(ordersRes.data);
      setSubscribers(subscribersRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSeries = async () => {
    try {
      const response = await axios.get(`${API}/series?gallery_type=nsfw`, getAuthHeaders());
      // New response format includes objects with name, preview_images, artwork_count
      const seriesNames = response.data.map(s => s.name || s);
      setExistingSeries(seriesNames);
    } catch (error) {
      console.error('Failed to fetch series:', error);
    }
  };

  const fetchSeriesWithDescriptions = async () => {
    try {
      const response = await axios.get(`${API}/series?gallery_type=nsfw`, getAuthHeaders());
      setSeriesWithDescriptions(response.data);
    } catch (error) {
      console.error('Failed to fetch series with descriptions:', error);
    }
  };

  const fetchContactMessages = async () => {
    try {
      const response = await axios.get(`${API}/contact/messages`, getAuthHeaders());
      setContactMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch contact messages:', error);
    }
  };

  const handleViewMessage = async (message) => {
    setSelectedMessage(message);
    setReplyText('');
    setMessageDialogOpen(true);
    
    // Mark as read if unread
    if (message.status === 'unread') {
      try {
        await axios.get(`${API}/contact/messages/${message.id}`, getAuthHeaders());
        fetchContactMessages();
      } catch (error) {
        console.error('Failed to mark message as read:', error);
      }
    }
  };

  const handleSendReply = async () => {
    if (!replyText.trim()) {
      toast.error('Please enter a reply message');
      return;
    }
    
    setSendingReply(true);
    try {
      await axios.post(
        `${API}/contact/messages/${selectedMessage.id}/reply`,
        { message: replyText },
        getAuthHeaders()
      );
      toast.success('Reply sent successfully');
      setMessageDialogOpen(false);
      setReplyText('');
      fetchContactMessages();
    } catch (error) {
      console.error('Failed to send reply:', error);
      toast.error(error.response?.data?.detail || 'Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    
    try {
      await axios.delete(`${API}/contact/messages/${messageId}`, getAuthHeaders());
      toast.success('Message deleted');
      fetchContactMessages();
    } catch (error) {
      console.error('Failed to delete message:', error);
      toast.error('Failed to delete message');
    }
  };

  const handleEditSeriesDescription = (series) => {
    setEditingSeriesName(series.name);
    setEditingSeriesDescription(series.description || '');
    setSeriesDialogOpen(true);
  };

  const handleSaveSeriesDescription = async () => {
    try {
      await axios.put(
        `${API}/series/${encodeURIComponent(editingSeriesName)}`,
        { description: editingSeriesDescription },
        getAuthHeaders()
      );
      toast.success('Series description updated');
      setSeriesDialogOpen(false);
      fetchSeriesWithDescriptions();
    } catch (error) {
      console.error('Failed to update series description:', error);
      toast.error('Failed to update series description');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate that either file or URL is provided
    if (!selectedFile && !formData.image_url) {
      toast.error('Please upload an image or provide an image URL');
      return;
    }
    
    try {
      // Upload image first if file is selected
      let imageUrl = formData.image_url;
      if (selectedFile) {
        setUploadingImage(true);
        
        const uploadFormData = new FormData();
        uploadFormData.append('file', selectedFile);
        
        try {
          const uploadResponse = await axios.post(
            `${API}/upload/image`,
            uploadFormData,
            {
              ...getAuthHeaders(),
              headers: {
                ...getAuthHeaders().headers,
                'Content-Type': 'multipart/form-data'
              }
            }
          );
          
          imageUrl = uploadResponse.data.image_url;
          setUploadingImage(false);
        } catch (uploadError) {
          console.error('Image upload failed:', uploadError.response?.data || uploadError.message);
          toast.error(uploadError.response?.data?.detail || 'Failed to upload image');
          setUploadingImage(false);
          return;
        }
      }

      if (editingArtwork) {
        await axios.put(
          `${API}/artworks/${editingArtwork.id}`,
          { 
            ...formData, 
            image_url: imageUrl, 
            price: parseFloat(formData.price),
            shipping_rate: parseFloat(formData.shipping_rate),
            quantity_available: parseInt(formData.quantity_available),
            series_name: formData.series_name || null
          },
          getAuthHeaders()
        );
        toast.success('Artwork updated');
      } else {
        await axios.post(
          `${API}/artworks`,
          { 
            ...formData, 
            image_url: imageUrl, 
            price: parseFloat(formData.price),
            shipping_rate: parseFloat(formData.shipping_rate),
            quantity_available: parseInt(formData.quantity_available),
            series_name: formData.series_name || null
          },
          getAuthHeaders()
        );
        toast.success('Artwork added');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
      fetchSeries(); // Refresh series list
    } catch (error) {
      console.error('Failed to save artwork:', error);
      toast.error(error.response?.data?.detail || 'Failed to save artwork');
      setUploadingImage(false);
    }
  };

  const handleEdit = (artwork) => {
    setEditingArtwork(artwork);
    setFormData({
      title: artwork.title,
      description: artwork.description,
      category: artwork.category,
      gallery_type: artwork.gallery_type || 'main',
      series_name: artwork.series_name || '',
      price: artwork.price.toString(),
      image_url: artwork.image_url,
      dimensions: artwork.dimensions,
      shipping_rate: artwork.shipping_rate?.toString() || '15.00',
      quantity_available: artwork.quantity_available?.toString() || '1',
      includes_certificate: artwork.includes_certificate !== false,
      is_framed: artwork.is_framed || false,
      is_signed: artwork.is_signed !== false
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this artwork?')) return;
    
    try {
      await axios.delete(`${API}/artworks/${id}`, getAuthHeaders());
      toast.success('Artwork deleted');
      fetchData();
    } catch (error) {
      console.error('Failed to delete artwork:', error);
      toast.error('Failed to delete artwork');
    }
  };

  const handleStatusChange = async (artwork, newStatus) => {
    try {
      const isAvailable = newStatus === 'available';
      await axios.put(
        `${API}/artworks/${artwork.id}`,
        {
          artwork_status: newStatus,
          is_available: isAvailable,
          quantity_available: isAvailable ? Math.max(artwork.quantity_available || 1, 1) : 0
        },
        getAuthHeaders()
      );
      const labels = { available: 'Available', reserved: 'Reserved', sold: 'Sold' };
      toast.success(`Marked as ${labels[newStatus]}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.patch(
        `${API}/orders/${orderId}/status?status=${newStatus}`,
        {},
        getAuthHeaders()
      );
      toast.success('Order status updated');
      fetchData();
    } catch (error) {
      console.error('Failed to update order status:', error);
      toast.error('Failed to update order status');
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this order? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/orders/${orderId}`, getAuthHeaders());
      toast.success('Order deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete order');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: 'painting',
      gallery_type: 'main',
      series_name: '',
      price: '',
      image_url: '',
      dimensions: '',
      shipping_rate: '15.00',
      quantity_available: '1',
      includes_certificate: true,
      is_framed: false,
      is_signed: true
    });
    setEditingArtwork(null);
    setSelectedFile(null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      // Clear the URL field when file is selected
      setFormData({ ...formData, image_url: '' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  const unreadCount = contactMessages.filter(m => m.status === 'unread').length;

  return (
    <div className="min-h-screen pt-32 pb-24" data-testid="artist-dashboard-page">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <h1 className="font-serif text-4xl md:text-5xl tracking-tight mb-12" data-testid="artist-dashboard-title">
          Artist Dashboard
        </h1>

        <Tabs defaultValue="artworks" className="w-full">
          <TabsList className="mb-8">
            <TabsTrigger value="artworks" data-testid="artworks-tab">Artworks</TabsTrigger>
            <TabsTrigger value="series" data-testid="series-tab">Series</TabsTrigger>
            <TabsTrigger value="messages" data-testid="messages-tab" className="relative">
              Messages
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="orders" data-testid="orders-tab">Orders</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="analytics-tab">Analytics</TabsTrigger>
            <TabsTrigger value="customers" data-testid="customers-tab">Customer Data</TabsTrigger>
            <TabsTrigger value="newsletter" data-testid="newsletter-tab">Newsletter</TabsTrigger>
          </TabsList>

          <TabsContent value="artworks">
            <div className="mb-6">
              <Dialog open={dialogOpen} onOpenChange={(open) => {
                setDialogOpen(open);
                if (!open) resetForm();
              }}>
                <DialogTrigger asChild>
                  <Button className="rounded-full" data-testid="add-artwork-btn">
                    <Plus className="mr-2 h-4 w-4" />
                    ADD NEW ARTWORK
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>
                      {editingArtwork ? 'Edit Artwork' : 'Add New Artwork'}
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <Label htmlFor="title">TITLE</Label>
                      <Input
                        id="title"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        required
                        data-testid="artwork-title-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="description">DESCRIPTION</Label>
                      <Textarea
                        id="description"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        required
                        rows={4}
                        data-testid="artwork-description-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="category">CATEGORY</Label>
                        <Select
                          value={formData.category}
                          onValueChange={(value) => setFormData({ ...formData, category: value })}
                        >
                          <SelectTrigger data-testid="artwork-category-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="painting">Painting</SelectItem>
                            <SelectItem value="photography">Photography</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="gallery_type">GALLERY</Label>
                        <Select
                          value={formData.gallery_type}
                          onValueChange={(value) => setFormData({ ...formData, gallery_type: value })}
                        >
                          <SelectTrigger data-testid="artwork-gallery-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="main">Main Gallery</SelectItem>
                            <SelectItem value="nsfw">NSFW Collection</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    {formData.gallery_type === 'nsfw' && (
                      <div>
                        <Label htmlFor="series_name">SERIES/FOLDER (Optional)</Label>
                        <div className="flex gap-2">
                          <Input
                            id="series_name"
                            value={formData.series_name}
                            onChange={(e) => setFormData({ ...formData, series_name: e.target.value })}
                            placeholder="e.g., 'Urban Series', 'Abstract Collection'"
                            data-testid="artwork-series-input"
                            list="existing-series"
                          />
                          <datalist id="existing-series">
                            {existingSeries.map((series) => (
                              <option key={series} value={series} />
                            ))}
                          </datalist>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Group related artworks together in the NSFW Collection
                        </p>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="price">PRICE ($)</Label>
                        <Input
                          id="price"
                          type="number"
                          step="0.01"
                          value={formData.price}
                          onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                          required
                          data-testid="artwork-price-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="quantity_available">QUANTITY AVAILABLE</Label>
                        <Input
                          id="quantity_available"
                          type="number"
                          min="0"
                          value={formData.quantity_available}
                          onChange={(e) => setFormData({ ...formData, quantity_available: e.target.value })}
                          required
                          data-testid="artwork-quantity-input"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          1 for originals, more for prints
                        </p>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="shipping_rate">SHIPPING RATE ($)</Label>
                      <Select
                        value={formData.shipping_rate}
                        onValueChange={(value) => setFormData({ ...formData, shipping_rate: value })}
                      >
                        <SelectTrigger data-testid="artwork-shipping-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0.00">Free Shipping</SelectItem>
                          <SelectItem value="5.00">$5.00 - Envelope/Small</SelectItem>
                          <SelectItem value="10.00">$10.00 - Flat Rate Small Box</SelectItem>
                          <SelectItem value="15.00">$15.00 - Flat Rate Medium Box</SelectItem>
                          <SelectItem value="20.00">$20.00 - Flat Rate Large Box</SelectItem>
                          <SelectItem value="25.00">$25.00 - Priority Mail</SelectItem>
                          <SelectItem value="35.00">$35.00 - Express Mail</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="image">ARTWORK IMAGE</Label>
                      <div className="space-y-3">
                        <div>
                          <Label htmlFor="image_file" className="text-sm text-muted-foreground">
                            Upload from Computer/Phone
                          </Label>
                          <Input
                            id="image_file"
                            type="file"
                            accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif"
                            onChange={handleFileChange}
                            className="cursor-pointer"
                            data-testid="artwork-image-file"
                          />
                          {selectedFile && (
                            <p className="text-sm text-muted-foreground mt-1">
                              Selected: {selectedFile.name}
                            </p>
                          )}
                        </div>
                        <div className="relative">
                          <div className="absolute inset-0 flex items-center">
                            <span className="w-full border-t" />
                          </div>
                          <div className="relative flex justify-center text-xs uppercase">
                            <span className="bg-background px-2 text-muted-foreground">
                              Or use URL
                            </span>
                          </div>
                        </div>
                        <div>
                          <Input
                            id="image_url"
                            value={formData.image_url}
                            onChange={(e) => {
                              setFormData({ ...formData, image_url: e.target.value });
                              setSelectedFile(null);
                            }}
                            placeholder="https://example.com/image.jpg"
                            disabled={!!selectedFile}
                            data-testid="artwork-image-url"
                          />
                        </div>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="dimensions">DIMENSIONS</Label>
                      <Input
                        id="dimensions"
                        value={formData.dimensions}
                        onChange={(e) => setFormData({ ...formData, dimensions: e.target.value })}
                        required
                        placeholder="e.g., 24 x 36 inches"
                        data-testid="artwork-dimensions-input"
                      />
                    </div>
                    <div>
                      <Label className="mb-3 block">ABOUT THIS PIECE OPTIONS</Label>
                      <div className="space-y-3 border border-border rounded-lg p-4">
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id="includes_certificate"
                            checked={formData.includes_certificate}
                            onChange={(e) => setFormData({ ...formData, includes_certificate: e.target.checked })}
                            className="w-4 h-4 rounded border-border"
                            data-testid="artwork-certificate-checkbox"
                          />
                          <Label htmlFor="includes_certificate" className="font-normal cursor-pointer">
                            Includes Certificate of Authenticity
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id="is_signed"
                            checked={formData.is_signed}
                            onChange={(e) => setFormData({ ...formData, is_signed: e.target.checked })}
                            className="w-4 h-4 rounded border-border"
                            data-testid="artwork-signed-checkbox"
                          />
                          <Label htmlFor="is_signed" className="font-normal cursor-pointer">
                            Signed by Artist
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id="is_framed"
                            checked={formData.is_framed}
                            onChange={(e) => setFormData({ ...formData, is_framed: e.target.checked })}
                            className="w-4 h-4 rounded border-border"
                            data-testid="artwork-framed-checkbox"
                          />
                          <Label htmlFor="is_framed" className="font-normal cursor-pointer">
                            Includes Frame
                          </Label>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <Button type="submit" className="flex-1" disabled={uploadingImage} data-testid="save-artwork-btn">
                        {uploadingImage ? 'UPLOADING IMAGE...' : editingArtwork ? 'UPDATE' : 'ADD ARTWORK'}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setDialogOpen(false)}
                        disabled={uploadingImage}
                        data-testid="cancel-artwork-btn"
                      >
                        CANCEL
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {artworks.length === 0 ? (
              <div className="text-center py-20 text-muted-foreground" data-testid="no-artworks-message">
                No artworks yet. Add your first artwork to get started.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="artworks-grid">
                {artworks.map((artwork) => (
                  <div key={artwork.id} className="border border-border rounded-lg overflow-hidden" data-testid={`artwork-item-${artwork.id}`}>
                    <img
                      src={artwork.image_url}
                      alt={artwork.title}
                      className="w-full aspect-[3/4] object-cover"
                    />
                    <div className="p-4">
                      <h3 className="font-serif text-xl mb-2">{artwork.title}</h3>
                      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">
                        {artwork.category}
                      </p>
                      <p className="text-lg font-medium mb-2">${artwork.price.toFixed(2)}</p>
                      <p className="text-sm text-muted-foreground mb-4">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          (artwork.artwork_status || 'available') === 'available' ? 'bg-green-100 text-green-800' :
                          (artwork.artwork_status || 'available') === 'reserved' ? 'bg-amber-100 text-amber-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {(artwork.artwork_status || 'available') === 'available' ? 'Available' :
                           (artwork.artwork_status || 'available') === 'reserved' ? 'Reserved' : 'SOLD'}
                        </span>
                      </p>
                      <div className="flex gap-2 mb-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(artwork)}
                          className="flex-1"
                          data-testid={`edit-artwork-${artwork.id}`}
                        >
                          <Edit className="mr-2 h-4 w-4" />
                          EDIT
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(artwork.id)}
                          className="hover:text-destructive"
                          data-testid={`delete-artwork-${artwork.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      <Select
                        value={artwork.artwork_status || 'available'}
                        onValueChange={(value) => handleStatusChange(artwork, value)}
                      >
                        <SelectTrigger className="w-full" data-testid={`artwork-status-select-${artwork.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="available">Available</SelectItem>
                          <SelectItem value="reserved">Reserved</SelectItem>
                          <SelectItem value="sold">Sold</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="series">
            <div className="mb-6">
              <h2 className="text-2xl font-serif mb-4">NSFW Series Descriptions</h2>
              <p className="text-muted-foreground mb-6">
                Add descriptions to your NSFW series to explain the artistic perspective to customers.
              </p>
            </div>

            {seriesWithDescriptions.length === 0 ? (
              <div className="text-center py-20 text-muted-foreground">
                No series found. Add artworks to the NSFW collection with a series name to create series.
              </div>
            ) : (
              <div className="space-y-4">
                {seriesWithDescriptions.map((series) => (
                  <div key={series.name} className="border border-border rounded-lg p-6">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-serif text-xl mb-2">{series.name}</h3>
                        <p className="text-sm text-muted-foreground mb-2">
                          {series.artwork_count} {series.artwork_count === 1 ? 'artwork' : 'artworks'}
                        </p>
                        {series.description ? (
                          <p className="text-muted-foreground whitespace-pre-line">{series.description}</p>
                        ) : (
                          <p className="text-muted-foreground italic">No description yet</p>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditSeriesDescription(series)}
                        data-testid={`edit-series-${series.name}`}
                      >
                        <FileText className="mr-2 h-4 w-4" />
                        {series.description ? 'EDIT' : 'ADD'} DESCRIPTION
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Series Description Edit Dialog */}
            <Dialog open={seriesDialogOpen} onOpenChange={setSeriesDialogOpen}>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Edit Series Description: {editingSeriesName}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="series_description">DESCRIPTION</Label>
                    <Textarea
                      id="series_description"
                      value={editingSeriesDescription}
                      onChange={(e) => setEditingSeriesDescription(e.target.value)}
                      rows={6}
                      placeholder="Describe the artistic perspective and themes of this series..."
                      data-testid="series-description-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      This description will appear at the top when customers view this series.
                    </p>
                  </div>
                  <div className="flex gap-4">
                    <Button onClick={handleSaveSeriesDescription} className="flex-1" data-testid="save-series-btn">
                      SAVE DESCRIPTION
                    </Button>
                    <Button variant="outline" onClick={() => setSeriesDialogOpen(false)}>
                      CANCEL
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>

          <TabsContent value="messages">
            <div className="mb-6">
              <h2 className="text-2xl font-serif mb-2">Customer Messages</h2>
              <p className="text-muted-foreground">
                Messages from the contact form. Click to view and reply.
              </p>
            </div>

            {contactMessages.length === 0 ? (
              <div className="text-center py-20 text-muted-foreground">
                No messages yet
              </div>
            ) : (
              <div className="space-y-4">
                {contactMessages.map((message) => (
                  <div 
                    key={message.id} 
                    className={`border rounded-lg p-6 cursor-pointer hover:border-accent transition-colors ${
                      message.status === 'unread' ? 'border-accent bg-accent/5' : 'border-border'
                    }`}
                    onClick={() => handleViewMessage(message)}
                    data-testid={`message-item-${message.id}`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-medium">{message.name}</span>
                          <span className="text-sm text-muted-foreground">{message.email}</span>
                          {message.status === 'unread' && (
                            <span className="bg-accent text-accent-foreground text-xs px-2 py-0.5 rounded-full">NEW</span>
                          )}
                          {message.status === 'replied' && (
                            <span className="bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded-full">REPLIED</span>
                          )}
                        </div>
                        <h3 className="font-serif text-lg mb-1">{message.subject}</h3>
                        <p className="text-muted-foreground text-sm line-clamp-2">{message.message}</p>
                      </div>
                      <div className="text-right ml-4">
                        <p className="text-xs text-muted-foreground">
                          {new Date(message.created_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </p>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteMessage(message.id);
                          }}
                          className="mt-2"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Message View/Reply Dialog */}
            <Dialog open={messageDialogOpen} onOpenChange={setMessageDialogOpen}>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                {selectedMessage && (
                  <>
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <MessageCircle className="h-5 w-5" />
                        {selectedMessage.subject}
                      </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4">
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span><strong>From:</strong> {selectedMessage.name}</span>
                        <span>{selectedMessage.email}</span>
                        <span>{new Date(selectedMessage.created_at).toLocaleString()}</span>
                      </div>
                      
                      <div className="bg-secondary/50 p-4 rounded-lg">
                        <p className="whitespace-pre-line">{selectedMessage.message}</p>
                      </div>

                      {/* Previous Replies */}
                      {selectedMessage.replies && selectedMessage.replies.length > 0 && (
                        <div className="space-y-3">
                          <h4 className="font-medium text-sm uppercase tracking-wider text-muted-foreground">Previous Replies</h4>
                          {selectedMessage.replies.map((reply) => (
                            <div key={reply.id} className="bg-accent/10 p-4 rounded-lg border-l-4 border-accent">
                              <div className="flex justify-between items-center mb-2">
                                <span className="text-sm font-medium">{reply.sent_by}</span>
                                <span className="text-xs text-muted-foreground">
                                  {new Date(reply.sent_at).toLocaleString()}
                                </span>
                              </div>
                              <p className="whitespace-pre-line text-sm">{reply.message}</p>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Reply Form */}
                      <div className="space-y-3 pt-4 border-t">
                        <Label htmlFor="reply">SEND A REPLY</Label>
                        <Textarea
                          id="reply"
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          rows={5}
                          placeholder="Type your reply here..."
                          data-testid="reply-textarea"
                        />
                        <div className="flex gap-3">
                          <Button 
                            onClick={handleSendReply} 
                            disabled={sendingReply}
                            className="flex-1"
                            data-testid="send-reply-btn"
                          >
                            {sendingReply ? (
                              'SENDING...'
                            ) : (
                              <>
                                <Send className="mr-2 h-4 w-4" />
                                SEND REPLY
                              </>
                            )}
                          </Button>
                          <Button variant="outline" onClick={() => setMessageDialogOpen(false)}>
                            CLOSE
                          </Button>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          <TabsContent value="orders">
            {orders.length === 0 ? (
              <div className="text-center py-20 text-muted-foreground" data-testid="no-orders-message">
                No orders yet
              </div>
            ) : (
              <div className="space-y-6">
                {orders.map((order) => (
                  <div key={order.id} className="border border-border rounded-lg p-6" data-testid={`order-item-${order.id}`}>
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">
                          ORDER #{order.id.slice(0, 8).toUpperCase()}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {new Date(order.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Customer: {order.user_email}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Select
                          value={order.status}
                          onValueChange={(value) => handleUpdateOrderStatus(order.id, value)}
                        >
                          <SelectTrigger className="w-[180px]" data-testid={`order-status-select-${order.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="paid">Paid</SelectItem>
                            <SelectItem value="processing">Processing</SelectItem>
                            <SelectItem value="shipped">Shipped</SelectItem>
                            <SelectItem value="delivered">Delivered</SelectItem>
                            <SelectItem value="cancelled">Cancelled</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteOrder(order.id)}
                          className="text-muted-foreground hover:text-destructive"
                          data-testid={`order-delete-btn-${order.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-3 mb-4">
                      {order.items.map((item) => (
                        <div key={item.artwork_id} className="flex gap-4">
                          <img
                            src={item.artwork_image}
                            alt={item.artwork_title}
                            className="w-16 h-20 object-cover"
                          />
                          <div className="flex-1">
                            <p className="font-medium">{item.artwork_title}</p>
                            <p className="text-sm text-muted-foreground">${item.price.toFixed(2)}</p>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="border-t border-border pt-4">
                      <div className="flex justify-between font-medium">
                        <span>Total</span>
                        <span>${order.total_amount.toFixed(2)}</span>
                      </div>
                    </div>

                    <div className="mt-4 pt-4 border-t border-border text-sm text-muted-foreground">
                      <p className="font-medium text-foreground mb-1">Shipping Address:</p>
                      <p>{order.shipping_address.name}</p>
                      <p>{order.shipping_address.address}</p>
                      <p>
                        {order.shipping_address.city}, {order.shipping_address.state}{' '}
                        {order.shipping_address.zip_code}
                      </p>
                      <p>{order.shipping_address.country}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="analytics">
            <AnalyticsTab getAuthHeaders={getAuthHeaders} />
          </TabsContent>

          <TabsContent value="customers">
            <div className="mb-6">
              <h2 className="text-2xl font-serif mb-4">Customer Purchase Data</h2>
              <p className="text-muted-foreground mb-6">
                Information collected from customer orders
              </p>
            </div>

            {orders.length === 0 ? (
              <div className="text-center py-20 text-muted-foreground">
                No customer data yet
              </div>
            ) : (
              <div className="border border-border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-secondary">
                      <tr>
                        <th className="text-left p-4 text-xs uppercase tracking-[0.2em]">Customer Name</th>
                        <th className="text-left p-4 text-xs uppercase tracking-[0.2em]">Email</th>
                        <th className="text-left p-4 text-xs uppercase tracking-[0.2em]">Address</th>
                        <th className="text-left p-4 text-xs uppercase tracking-[0.2em]">Purchase Date</th>
                        <th className="text-left p-4 text-xs uppercase tracking-[0.2em]">Total Spent</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((order) => (
                        <tr key={order.id} className="border-t border-border hover:bg-muted/50">
                          <td className="p-4">{order.shipping_address.name}</td>
                          <td className="p-4">{order.user_email}</td>
                          <td className="p-4 text-sm">
                            {order.shipping_address.address}, {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip_code}
                          </td>
                          <td className="p-4 text-sm">
                            {new Date(order.created_at).toLocaleDateString()}
                          </td>
                          <td className="p-4 font-medium">${order.total_amount.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="newsletter">
            <div className="mb-6">
              <h2 className="text-2xl font-serif mb-4">Newsletter Subscribers</h2>
              <p className="text-muted-foreground mb-2">
                Total Subscribers: <span className="font-medium text-foreground">{subscribers.length}</span>
              </p>
              <p className="text-sm text-muted-foreground mb-6">
                Export this data to your email marketing platform
              </p>
            </div>

            {subscribers.length === 0 ? (
              <div className="text-center py-20 text-muted-foreground">
                No newsletter subscribers yet
              </div>
            ) : (
              <div className="border border-border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-secondary">
                      <tr>
                        <th className="text-left p-4 text-xs uppercase tracking-[0.2em]">First Name</th>
                        <th className="text-left p-4 text-xs uppercase tracking-[0.2em]">Last Name</th>
                        <th className="text-left p-4 text-xs uppercase tracking-[0.2em]">Email</th>
                        <th className="text-left p-4 text-xs uppercase tracking-[0.2em]">Subscribed Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscribers.map((subscriber) => (
                        <tr key={subscriber.id} className="border-t border-border hover:bg-muted/50">
                          <td className="p-4">{subscriber.first_name}</td>
                          <td className="p-4">{subscriber.last_name}</td>
                          <td className="p-4">{subscriber.email}</td>
                          <td className="p-4 text-sm">
                            {new Date(subscriber.subscribed_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
